import { useCallback, useEffect } from 'react';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useActiveViewableAssetId } from '@frameio/shared/hooks';
import { useActiveDocumentId } from '../../../shared/contexts/ActiveHostEntitiesContext';
import { useDatadogLogger } from '../../../shared/hooks/useDatadogLogger';
import { usePluginFeatures } from '../../../shared/hooks/usePluginFeatures';
import {
  useGetPlayheadSyncStatusQuery,
  useSetPlayheadSyncStatusMutation,
} from '../../gql';

const UXP_CONTEXT = { resolveWithUXP: true } as const;

/**
 * Manages the enabled/disabled state of playhead sync between Frame.io and
 * Premiere Pro. Provides current status and a toggle function with optimistic
 * updates.
 *
 * @returns Object containing:
 *   - playheadSyncEnabled: whether playhead sync is currently enabled
 *   - onPlayheadSyncClick: callback to toggle sync on/off
 *   - isLoading: Loading state for query or mutation
 */
export function usePremierePlayheadSyncStatus() {
  const logger = useDatadogLogger();
  const isWebClient = useIsMatchingClientName('web');
  const sequenceId = useActiveDocumentId();
  const assetId = useActiveViewableAssetId();
  const { canSyncPlayhead } = usePluginFeatures();

  const skipQuery = isWebClient || !sequenceId || !assetId || !canSyncPlayhead;

  const {
    data,
    loading: getPlayheadSyncStatusLoading,
    error: getPlayheadSyncStatusError,
  } = useGetPlayheadSyncStatusQuery({
    context: UXP_CONTEXT,
    skip: skipQuery,
    variables: { assetId, sequenceId },
  });

  useEffect(() => {
    if (!getPlayheadSyncStatusError) return;
    logger.error('GetPlayheadSyncStatus query failed', {
      error: getPlayheadSyncStatusError.message,
      sequenceId,
      assetId,
    });
  }, [getPlayheadSyncStatusError, sequenceId, assetId, logger]);

  const playheadSyncStatus = data?.getPlayheadSyncStatus;

  const [setPlayheadSyncStatus, { loading: setPlayheadSyncStatusLoading }] =
    useSetPlayheadSyncStatusMutation({
      context: UXP_CONTEXT,
      onError: (mutError) => {
        logger.error('setPlayheadSyncStatus mutation failed', {
          error: mutError.message,
          sequenceId,
          assetId,
        });
      },
    });

  const onPlayheadSyncClick = useCallback(() => {
    if (!sequenceId || !assetId) {
      logger.error('PlayheadSync failed: missing assetId or sequenceId', {
        sequenceId,
        assetId,
      });
      return;
    }

    const nextStatus =
      playheadSyncStatus?.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';

    setPlayheadSyncStatus({
      optimisticResponse: {
        __typename: 'Mutation',
        setPlayheadSyncStatus: {
          __typename: 'PlayheadSyncStatus',
          status: nextStatus,
          assetId,
          sequenceId,
        },
      },
      refetchQueries: ['GetPlayheadSyncStatus'],
      variables: {
        input: {
          status: nextStatus,
          assetId,
          sequenceId,
        },
      },
    });
  }, [
    playheadSyncStatus?.status,
    setPlayheadSyncStatus,
    sequenceId,
    assetId,
    logger,
  ]);

  return {
    canSyncPlayhead,
    playheadSyncEnabled: playheadSyncStatus?.status === 'ENABLED',
    onPlayheadSyncClick,
    isLoading: getPlayheadSyncStatusLoading || setPlayheadSyncStatusLoading,
  };
}
